import { Pool } from "pg";
import {
  ActionFunction,
  Form,
  Link,
  LoaderFunction,
  redirect,
  useActionData,
  useTransition,
} from "remix";
import { OptionalUser } from "~/lib/auth.server";
import { Button, ModalBoxOutline, NarrowPageContents, PageHeader } from "~/lib/components";
import { BasicPage } from "~/lib/components/BasicPage";
import { FillVerifyUserTemplate } from "~/lib/email/templates/verifyUser";
import { issueToken } from "~/new_shared/authShared.server";
import { RequestType, writeActionRequest } from "~/new_shared/db/actionRequest";
import { getPool } from "~/new_shared/db/db.server";
import {
  FullUser,
  getPrimaryUserEmailAddress,
  getUserByUsernameOrEmail,
  GetUserIfValidUsernameOrEmailForLogin,
} from "~/new_shared/db/user.server";
import { getDoActionRequest } from "~/new_shared/effects.server";
import { sendEmailRaw, getEmailServer } from "~/new_shared/email/sendEmail.server";
import { TntAllUrls } from "~/new_shared/urls";

interface ActionData {
  requestSuccess: boolean;
  message?: string;
  emailDomain?: string;
}

interface Props {
  actionData: ActionData | undefined;
}

export const loader: LoaderFunction = async ({ request }) => {
  const { user } = await OptionalUser(request);
  if (user) {
    return redirect(TntAllUrls.Inbox());
  }
  return {};
};

export function SigninPrompt({ actionData }: Props) {
  const transition = useTransition();
  return (
    <Form method="post">
      <fieldset disabled={transition.state == "submitting"}>
        <h3 className="text-lg leading-6 font-medium text-gray-900">Sign in</h3>
        <div className="mt-2 max-w-xl text-sm text-gray-500">
          <p>Please enter your username or email address</p>
        </div>
        <div className="mt-5 sm:flex  relative">
          <div className="w-full sm:max-w-xs">
            <label htmlFor="usernameOrEmail" className="sr-only">
              Username or non-ThxNoThx Email Address
            </label>
            <input
              type="text"
              name="usernameOrEmail"
              id="email"
              className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="you@gmail.com"
            />
            {!actionData?.requestSuccess && (
              <div className="text-red-500">{actionData?.message}</div>
            )}
          </div>
          <div className="pt-8 sm:pl-8 sm:pt-0">
            <Button>Submit</Button>
          </div>
        </div>
      </fieldset>
    </Form>
  );
}

export default function Login() {
  const actionData = useActionData<ActionData>();

  return (
    <BasicPage user={undefined}>
      <NarrowPageContents>
        <ModalBoxOutline>
          {actionData?.requestSuccess ? (
            <div className="mx-auto">
              <p>
                We have a sent an email with a login link to{" "}
                {actionData.emailDomain ? (
                  <>your {actionData.emailDomain} email address</>
                ) : (
                  <>the email address where you normally receive ThxNoThx emails.</>
                )}
              </p>
            </div>
          ) : (
            <SigninPrompt actionData={actionData} />
          )}
        </ModalBoxOutline>
        <p className="mt-16">
          ThxNoThx does not use passwords - when you enter your username or non-ThxNoThx email
          address above, we'll send you an email with a magic link - you can then click that link
          and you'll be signed in on that device.
        </p>
        <p>
          We'll keep you signed in on that device until you sign out - we recommend just leaving
          yourself logged into the site so it's easy to come back here.
        </p>
        <p>
          <Link to={TntAllUrls.Faq("passwords")}>Learn more</Link>
        </p>
      </NarrowPageContents>
    </BasicPage>
  );
}

export const action: ActionFunction = async ({ request }): Promise<ActionData> => {
  const form = await request.formData();
  const pool = await getPool();

  const usernameOrEmail = form.get("usernameOrEmail") as string;
  if (!usernameOrEmail) {
    return { requestSuccess: false, message: "You must enter a value" };
  }
  const user = await GetUserIfValidUsernameOrEmailForLogin(pool, usernameOrEmail);
  if (user) {
    console.log("Sending login link to user ", user.id);
    const email = FillVerifyUserTemplate(await issueToken(user.id));
    await sendEmailRaw(
      pool,
      await getEmailServer(),
      console.log,
      getDoActionRequest(pool),
      email,
      user,
    );
    const fullEmailAddress = await getPrimaryUserEmailAddress(pool, user.id);
    const genericmailDomains = [
      "hotmail.com",
      "outlook.com",
      "gmail.com",
      "aol.com",
      "icloud.com",
      "live.com",
      "yahoo.com",
      "ymail.com",
    ];

    let emailDomain = genericmailDomains.find((domain) => fullEmailAddress.includes(domain));
    // we don't automatically include the domain name since some domain names
    // are unique identifiers (eg stephensisk.net)

    // previously we didn't return any information at all, but with people
    // having multiple email addresses, it was getting a bit confusing where emails
    // were getting sent

    // also facebook discloses whether or not an email address is used on facebook,
    // so :shrug:? Seems like this is a privacy/usability tradeoff, and I'm choosing
    // usability for now.
    return { requestSuccess: true, emailDomain };
  } else {
    await writeActionRequest(
      pool,
      RequestType.UnexpectedActivity,
      "failed login attempt with usernameOrEmail",
      { usernameOrEmail },
    );
    return {
      requestSuccess: false,
      message: `Could not find a user for that information - try putting
        in your regular email address where you normally receive
        ThxNoThx emails (eg bob@gmail.com)`,
    };
  }
};
